import { useEffect, useRef, useState, FormEvent } from 'react';
import Button from '@components/Button';
import {
  listVisionBoardBackgrounds,
  createVisionBoardBackground,
  updateVisionBoardBackground,
  setVisionBoardBackgroundDefault,
  deleteVisionBoardBackground,
  type NewVisionBoardBackground,
} from '@lib/visionBoardBackgrounds';
import type { VisionBoardBackground } from '@lib/api';

type BackgroundFormState = {
  title: string;
  image_url: string;
  active: boolean;
  is_default: boolean;
  default_display: 'fill' | 'repeat';
};

const blankForm: BackgroundFormState = {
  title: '',
  image_url: '',
  active: true,
  is_default: false,
  default_display: 'fill',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminVisionBoardBackgrounds() {
  const [backgrounds, setBackgrounds] = useState<VisionBoardBackground[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<VisionBoardBackground | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState<VisionBoardBackground | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BackgroundFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listVisionBoardBackgrounds();
        if (isMounted) setBackgrounds(data);
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : String(err ?? 'Unable to load backgrounds')
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const openAdd = () => {
    setEditing(null);
    setFormData(blankForm);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (background: VisionBoardBackground) => {
    setEditing(background);
    setFormError(null);
    setFormData({
      title: background.title,
      image_url: background.image_url,
      active: background.active,
      is_default: background.is_default,
      default_display: background.default_display ?? 'fill',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
  };

  const openDelete = (background: VisionBoardBackground) => {
    setDeleting(background);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeleting(null);
    setDeleteError(null);
  };

  const handleInputChange = (
    field: keyof BackgroundFormState,
    value: string | boolean
  ) => {
    setFormData((prev) => {
      if (field === 'is_default' && value === true) {
        return { ...prev, is_default: true, active: true };
      }
      if (field === 'active' && value === false) {
        return { ...prev, active: false, is_default: false };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload: NewVisionBoardBackground = {
      title: formData.title.trim(),
      image_url: formData.image_url.trim(),
      active: formData.active || formData.is_default,
      is_default: formData.is_default,
      default_display: formData.default_display,
    };

    try {
      if (!payload.title) throw new Error('Title is required');
      if (!payload.image_url) throw new Error('Image URL is required');
      if (editing) {
        const updated = await updateVisionBoardBackground(
          editing.id,
          payload.is_default ? { ...payload, is_default: false } : payload
        );
        if (payload.is_default) {
          const defaulted = await setVisionBoardBackgroundDefault(updated.id);
          setBackgrounds((prev) =>
            prev.map((item) =>
              item.id === defaulted.id
                ? defaulted
                : { ...item, is_default: false }
            )
          );
        } else {
          setBackgrounds((prev) =>
            prev.map((item) => (item.id === updated.id ? updated : item))
          );
        }
      } else {
        const created = await createVisionBoardBackground({
          ...payload,
          is_default: false,
        });
        if (payload.is_default) {
          const defaulted = await setVisionBoardBackgroundDefault(created.id);
          setBackgrounds((prev) => [
            defaulted,
            ...prev.map((item) => ({ ...item, is_default: false })),
          ]);
        } else {
          setBackgrounds((prev) => [created, ...prev]);
        }
      }
      setIsModalOpen(false);
      setEditing(null);
      setFormData(blankForm);
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to save background')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteVisionBoardBackground(deleting.id);
      setBackgrounds((prev) => prev.filter((b) => b.id !== deleting.id));
      setShowDeleteModal(false);
      setDeleting(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to delete')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Vision Board Backgrounds
          </h1>
          <p className="text-lg text-gray-600">
            Manage preset canvas background images for Vision Boards.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {backgrounds.length}
              </span>{' '}
              total
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div />
            <Button
              type="button"
              onClick={openAdd}
              className="item-selected inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Add Background
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : backgrounds.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No vision board backgrounds yet. Add one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 divide-y divide-white/40 lg:hidden">
                  {backgrounds.map((background) => (
                    <div
                      key={background.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className="h-16 w-24 overflow-hidden rounded-lg border border-gray-200 bg-cover bg-center"
                            style={{
                              backgroundImage: `url(${background.image_url})`,
                            }}
                          />
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              {background.title}
                            </h2>
                            <span
                              className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${background.active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
                            >
                              {background.active ? 'Active' : 'Not active'}
                            </span>
                            {background.is_default && (
                              <span className="ml-2 inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
                                Default
                              </span>
                            )}
                            <span className="ml-2 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                              {background.default_display === 'repeat'
                                ? 'Repeat'
                                : 'Fill'}
                            </span>
                            <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">
                              Created {formatDate(background.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                          <ActionsMenu
                            id={background.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEdit(background),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDelete(background),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Preview</th>
                          <th className="px-4 py-3">Title</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Default</th>
                          <th className="px-4 py-3">Display</th>
                          <th className="px-4 py-3">Created</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backgrounds.map((background) => (
                          <tr
                            key={background.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div
                                className="h-12 w-20 overflow-hidden rounded-lg border border-gray-200 bg-cover bg-center"
                                style={{
                                  backgroundImage: `url(${background.image_url})`,
                                }}
                              />
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div className="font-semibold">
                                {background.title}
                              </div>
                              <div className="text-xs text-gray-500">
                                {background.image_url}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${background.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {background.active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${background.is_default ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
                              >
                                {background.is_default ? 'Default' : '--'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                {background.default_display === 'repeat'
                                  ? 'Repeat'
                                  : 'Fill'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {formatDate(background.created_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  id={background.id}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEdit(background),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDelete(background),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close background modal"
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editing ? 'Edit Background' : 'Add Background'}
            </h2>
            <p className="text-sm text-gray-500">
              {editing
                ? 'Update this preset background for the vision board editor.'
                : 'Add a preset background image for the vision board editor.'}
            </p>
            {formError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSave}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div>
                  <label
                    htmlFor="background-title"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Title
                  </label>
                  <input
                    id="background-title"
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      handleInputChange('title', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="background-image-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Image URL
                  </label>
                  <input
                    id="background-image-url"
                    type="text"
                    value={formData.image_url}
                    onChange={(e) =>
                      handleInputChange('image_url', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      handleInputChange('active', e.target.checked)
                    }
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Active (visible in presets)
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) =>
                      handleInputChange('is_default', e.target.checked)
                    }
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Default background (selected in settings)
                </label>
                <div>
                  <label
                    htmlFor="background-default-display"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Default Display
                  </label>
                  <select
                    id="background-default-display"
                    value={formData.default_display}
                    onChange={(e) =>
                      handleInputChange(
                        'default_display',
                        e.target.value as 'fill' | 'repeat'
                      )
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="fill">Fill (best for photos)</option>
                    <option value="repeat">Repeat (best for patterns)</option>
                  </select>
                </div>
                {formData.image_url.trim() && (
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div
                      className="h-16 w-24 overflow-hidden rounded-md border border-gray-200 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${formData.image_url})`,
                      }}
                    />
                    <div className="text-xs text-gray-500">
                      Preview of the background image.
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving
                    ? 'Saving...'
                    : editing
                      ? 'Save Changes'
                      : 'Add Background'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDelete}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete background modal"
              disabled={isDeleting}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              Delete Background
            </h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to remove this background? This will no
              longer be available for Vision Boards.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisionBoardBackgrounds;


